import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../../../services/api';
import LogoutButton from '../../../components/LogoutButton';
import BrandNotificationBell from '../../../components/BrandNotificationBell';
import CreditsWidget from '../../../components/CreditsWidget';
import ProfileAvatar from '../../../components/ProfileAvatar';

interface HeaderProps {
  darkMode: boolean;
  setDarkMode: (darkMode: boolean) => void;
  pageTitle?: string;
  pageSubtitle?: string;
}

export default function Header({ darkMode, setDarkMode, pageTitle, pageSubtitle }: HeaderProps) {
  const navigate = useNavigate();
  // const [showNotifications, setShowNotifications] = useState(false);

  // const notifications = [
  //   {
  //     id: 1,
  //     type: 'success' as const,
  //     title: 'Post published successfully',
  //     message: 'LinkedIn article "AI in Marketing" went live and reached 2.4K impressions',
  //     time: '1 hour ago',
  //   },
  //   {
  //     id: 2,
  //     type: 'warning' as const,
  //     title: 'Content backlog alert',
  //     message: '3 posts in Implementation are overdue by 2 days',
  //     time: '3 hours ago',
  //   },
  //   {
  //     id: 3,
  //     type: 'info' as const,
  //     title: 'Engagement spike detected',
  //     message: 'Instagram carousel "Product Tips" got 3.2x more engagement than average',
  //     time: '5 hours ago',
  //   },
  //   {
  //     id: 4,
  //     type: 'success' as const,
  //     title: 'Strategy recalibration complete',
  //     message: 'Optimization engine updated content mix based on last month\'s performance',
  //     time: '8 hours ago',
  //   },
  // ];

  // const getIconClass = (type: string) => {
  //   switch (type) {
  //     case 'success': return 'ri-checkbox-circle-line text-emerald-500';
  //     case 'warning': return 'ri-alert-line text-amber-500';
  //     case 'info': return 'ri-information-line text-sky-500';
  //     default: return 'ri-notification-line text-gray-500';
  //   }
  // };

  // const getBgClass = (type: string) => {
  //   switch (type) {
  //     case 'success': return 'bg-emerald-50 dark:bg-emerald-900/20';
  //     case 'warning': return 'bg-amber-50 dark:bg-amber-900/20';
  //     case 'info': return 'bg-sky-50 dark:bg-sky-900/20';
  //     default: return 'bg-gray-50 dark:bg-gray-800';
  //   }
  // };
  const [userName, setUserName] = useState<string>("Loading...");
  const [brandName, setBrandName] = useState<string>(() => localStorage.getItem('brand_name') || '');
  const [logoUrl, setLogoUrl] = useState<string | null>(() => {
    const storedLogo = localStorage.getItem("logo_url");
    return storedLogo ? storedLogo.replace(/"/g, '') || null : null;
  });
  useEffect(() => {
    async function fetchUserAndBrand() {
      const userId = localStorage.getItem("user_id");
      if (!userId) {
        setUserName(localStorage.getItem("full_name") || localStorage.getItem("username") || "User");
        return;
      }
      try {
        const [profile, bizProfile] = await Promise.allSettled([
          apiService.getContactIdentity(userId),
          apiService.getWebsiteProfile(userId),
        ]);

        if (profile.status === 'fulfilled') {
          setUserName(
            profile.value?.data?.full_name ||
            localStorage.getItem("full_name") ||
            localStorage.getItem("username") ||
            "User"
          );
        } else {
          setUserName(
            localStorage.getItem("full_name") ||
            localStorage.getItem("username") ||
            "User"
          );
        }

        if (bizProfile.status === 'fulfilled') {
          const bData = bizProfile.value?.data?.business_profile || bizProfile.value?.business_profile || bizProfile.value;
          if (bData?.business_name) {
            setBrandName(bData.business_name);
            localStorage.setItem('brand_name', bData.business_name);
          }
          if (bData?.logo_url) {
            const cleanedLogo = bData.logo_url.replace(/"/g, '');
            localStorage.setItem('logo_url', cleanedLogo);
            setLogoUrl(cleanedLogo);
          }
        }
      } catch (err) {
        const raw = localStorage.getItem("logo_url");
        setLogoUrl(raw ? raw.replace(/"/g, '') || null : null);
        setUserName(localStorage.getItem("full_name") || "User");
      }
    }
    fetchUserAndBrand();

    const handleLogoUpdated = () => {
      setLogoUrl((localStorage.getItem('logo_url') || '').replace(/"/g, ''));
      setBrandName(localStorage.getItem('brand_name') || '');
    };
    window.addEventListener('logo_updated', handleLogoUpdated);
    return () => window.removeEventListener('logo_updated', handleLogoUpdated);
  }, []);

  return (
    <header className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 h-[88px] flex items-center`}>
      <div className="flex items-center justify-between w-full">
        {/* Left */}
        <div>
          <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Social Media Manager
          </h2>
        </div>

        {/* Right */}
        <div className="flex items-center space-x-4">

          <div className="flex items-center space-x-4">
            {/* Navigation Links */}
            <div className="flex items-center space-x-2 mr-2">
              <Link
                to="/marketing-dashboard"
                className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-all text-sm font-semibold border ${darkMode
                  ? 'text-gray-400 border-gray-700 hover:text-white hover:bg-gray-700'
                  : 'text-gray-500 border-gray-100 hover:text-[#5C4FCF] hover:bg-[#5C4FCF]/5'
                  }`}
              >
                <i className="ri-home-4-line text-lg"></i>
                <span className="hidden lg:inline">Home</span>
              </Link>
            </div>

            <BrandNotificationBell />

            {/* Credits Widget */}
            <CreditsWidget />

            {/* Brand Profile Button */}
            <button
              onClick={() => navigate('/profile')}
              className={`flex items-center gap-2 pl-1 pr-3 py-1 rounded-full transition-colors cursor-pointer group ${
                darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
              title="Profile"
            >
              <ProfileAvatar logoUrl={logoUrl} fallbackName={brandName || userName} size="sm" />
              <span className={`text-sm font-medium whitespace-nowrap ${
                darkMode ? 'text-gray-200 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'
              }`}>
                Profile
              </span>
              <i className="ri-arrow-right-s-line text-gray-400 text-base"></i>
            </button>

            <LogoutButton
              className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-all text-sm font-semibold border ${darkMode
                ? 'text-gray-400 border-gray-700 hover:text-white hover:bg-gray-700'
                : 'text-gray-500 border-gray-100 hover:text-[#5C4FCF] hover:bg-[#5C4FCF]/5'
                }`}
            >
              <i className="ri-logout-box-r-line text-lg"></i>
              <span className="hidden lg:inline">Logout</span>
            </LogoutButton>

          </div>
        </div>
      </div>
    </header>
  );
}